import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const EmailThreadPane = ({ isOpen, onClose, emailData }) => {
  const [replyText, setReplyText] = useState('');
  const [showCannedResponses, setShowCannedResponses] = useState(false);
  const [additionalRecipients, setAdditionalRecipients] = useState('');
  const [status, setStatus] = useState(emailData?.status || 'Pending');
  const [newTag, setNewTag] = useState('');
  const [tags, setTags] = useState(emailData?.tags || []);

  if (!isOpen || !emailData) return null;

  const cannedResponses = [
    { id: 1, title: 'Thank you for contacting us', body: 'Thank you for reaching out to us. We have received your inquiry and will get back to you shortly.' },
    { id: 2, title: 'Issue resolved', body: 'We are pleased to inform you that your issue has been resolved. Please let us know if you need any further assistance.' },
    { id: 3, title: 'Request more information', body: 'To better assist you, could you please provide more details about your concern?' },
    { id: 4, title: 'Escalation notice', body: 'Your case has been escalated to our specialized team. They will contact you within 24 hours.' },
    { id: 5, title: 'Refund processed', body: 'Your refund has been processed and will appear in your account within 3-5 business days.' }
  ];

  const statusOptions = [
    { value: 'Pending', label: 'Pending' },
    { value: 'Closed', label: 'Closed' },
    { value: 'Resolved', label: 'Resolved' }
  ];

  const handleAddTag = () => {
    if (newTag?.trim() && !tags?.includes(newTag?.trim())) {
      setTags([...tags, newTag?.trim()]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags?.filter(tag => tag !== tagToRemove));
  };

  const handleCannedResponseSelect = (response) => {
    setReplyText(response?.body);
    setShowCannedResponses(false);
  };

  const handleSendReply = () => {
    // Send reply logic
    setReplyText('');
    setAdditionalRecipients('');
  };

  const handleCallCustomer = () => {
    // Call customer logic
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 z-[400] transition-opacity"
        onClick={onClose}
      />
      <div className="fixed top-0 right-0 h-full w-full md:w-[700px] lg:w-[800px] bg-card border-l border-border z-[500] overflow-y-auto shadow-2xl transform transition-transform">
        <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                <Icon name="Mail" size={20} className="text-primary" />
              </div>
              <div>
                <h2 className="text-lg md:text-xl font-semibold text-foreground">Email Thread View</h2>
                <p className="text-xs md:text-sm text-muted-foreground">{emailData?.id}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="flex items-center justify-center w-10 h-10 rounded-lg hover:bg-muted transition-base"
              aria-label="Close panel"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        <div className="p-4 md:p-6 space-y-6">
          <div className="bg-muted/30 rounded-lg p-4 border border-border">
            <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Info" size={16} className="text-primary" />
              Email Metadata
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Customer Name</p>
                <p className="text-sm font-medium text-foreground">{emailData?.customerName}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Mobile Number</p>
                <p className="text-sm font-medium text-foreground font-mono">{emailData?.mobileNumber}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Concern Category</p>
                <p className="text-sm font-medium text-foreground">{emailData?.concernCategory}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Email Category</p>
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                  {emailData?.emailCategory}
                </span>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Product</p>
                <p className="text-sm font-medium text-foreground">{emailData?.product}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Ticket ID</p>
                <p className="text-sm font-medium text-accent font-mono">{emailData?.ticketId || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">AI Agent</p>
                <p className="text-sm font-medium text-foreground">{emailData?.aiAgent}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Status</p>
                <Select
                  options={statusOptions}
                  value={status}
                  onChange={setStatus}
                  className="w-full"
                />
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Icon name="MessageSquare" size={16} className="text-primary" />
                Email Thread
              </h3>
            </div>
            <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
              {emailData?.emailThread?.map((email, index) => (
                <div
                  key={index}
                  className="bg-card rounded-lg p-4 border border-border hover:shadow-md transition-base"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Icon name="User" size={14} className="text-muted-foreground" />
                        <p className="text-xs font-semibold text-foreground">{email?.from}</p>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Icon name="ArrowRight" size={12} />
                        <span>To: {email?.to}</span>
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {new Date(email?.date)?.toLocaleString('en-US', { 
                        month: 'short', 
                        day: 'numeric', 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </span>
                  </div>
                  <p className="text-sm font-medium text-foreground mb-2">{email?.subject}</p>
                  <p className="text-sm text-muted-foreground mb-3">{email?.body}</p>
                  {email?.attachments?.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {email?.attachments?.map((attachment, idx) => (
                        <button
                          key={idx}
                          className="flex items-center gap-1 px-2 py-1 rounded bg-muted hover:bg-muted/80 text-xs text-foreground transition-base"
                        >
                          <Icon name="Paperclip" size={12} />
                          {attachment}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="bg-muted/30 rounded-lg p-4 border border-border">
            <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Reply" size={16} className="text-primary" />
              Reply to Customer
            </h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Additional Recipients (CC)</label>
                <input
                  type="text"
                  value={additionalRecipients}
                  onChange={(e) => setAdditionalRecipients(e?.target?.value)}
                  placeholder="email1@example.com, email2@example.com"
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs text-muted-foreground">Message</label>
                  <Button
                    variant="outline"
                    size="xs"
                    iconName="FileText"
                    iconPosition="left"
                    onClick={() => setShowCannedResponses(!showCannedResponses)}
                  >
                    Canned Responses
                  </Button>
                </div>
                {showCannedResponses && (
                  <div className="mb-2 p-2 bg-card border border-border rounded-lg max-h-40 overflow-y-auto">
                    {cannedResponses?.map((response) => (
                      <button
                        key={response?.id}
                        onClick={() => handleCannedResponseSelect(response)}
                        className="w-full text-left px-3 py-2 text-xs hover:bg-muted rounded transition-base"
                      >
                        <p className="font-medium text-foreground">{response?.title}</p>
                        <p className="text-muted-foreground truncate">{response?.body}</p>
                      </button>
                    ))}
                  </div>
                )}
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e?.target?.value)}
                  placeholder="Type your reply here..."
                  rows={6}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="default"
                  fullWidth
                  iconName="Send"
                  iconPosition="left"
                  onClick={handleSendReply}
                  disabled={!replyText?.trim()}
                >
                  Send Reply
                </Button>
                <Button
                  variant="outline"
                  iconName="Phone"
                  onClick={handleCallCustomer}
                  title="Call Customer"
                >
                  Call
                </Button>
              </div>
            </div>
          </div>

          <div className="bg-muted/30 rounded-lg p-4 border border-border">
            <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Tag" size={16} className="text-primary" />
              Tags Management
            </h3>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-muted-foreground mb-2">Existing Tags</p>
                <div className="flex flex-wrap gap-2">
                  {tags?.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium"
                    >
                      {tag}
                      <button
                        onClick={() => handleRemoveTag(tag)}
                        className="hover:text-error transition-base"
                      >
                        <Icon name="X" size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-2">Add New Tag</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e?.target?.value)}
                    onKeyDown={(e) => e?.key === 'Enter' && handleAddTag()}
                    placeholder="Enter tag name"
                    className="flex-1 px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <Button
                    variant="default"
                    size="sm"
                    iconName="Plus"
                    onClick={handleAddTag}
                  >
                    Add
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EmailThreadPane;